// =====================================================================
// src/screens/v2/BusinessHoursEditor.jsx
//
// Phase 6.0c.1 — Per-day hours editor accessible from the Manage tab.
//
// Supports the existing `hours` schema (see RegisterBusiness.jsx):
//   { mode: '247' }                                  — always open
//   { mode: 'appointment' }                          — by-appointment
//   { mode: 'standard', open, close, days: [...] }   — same hours across selected days
//   { mode: 'custom', schedule: { mon: {open,close}, ... } } — per-day
//
// Layout: full-screen overlay with header (close + Save), mode picker,
// and below it the relevant body (time pickers or per-day grid).
// =====================================================================

import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';

const DAYS = [
  { id: 'mon', label: 'Mon' },
  { id: 'tue', label: 'Tue' },
  { id: 'wed', label: 'Wed' },
  { id: 'thu', label: 'Thu' },
  { id: 'fri', label: 'Fri' },
  { id: 'sat', label: 'Sat' },
  { id: 'sun', label: 'Sun' },
];

const MODES = [
  { id: 'standard',    label: 'Standard hours',  hint: 'Same hours every working day' },
  { id: 'custom',      label: 'Custom per day',  hint: 'Different hours each day' },
  { id: '247',         label: 'Open 24/7',       hint: 'Always available' },
  { id: 'appointment', label: 'By appointment',  hint: 'No fixed hours' },
];

// Pull the editor's local form state from a stored hours object. Defensive —
// reads any shape and fills with sensible defaults.
function readHours(hours) {
  const safe = hours || {};
  const mode = ['247', 'appointment', 'standard', 'custom'].includes(safe.mode)
    ? safe.mode : 'standard';

  if (mode === '247' || mode === 'appointment') {
    return { mode, open: '10:00', close: '21:00', days: ['mon','tue','wed','thu','fri','sat'], schedule: {} };
  }
  if (mode === 'standard') {
    return {
      mode,
      open: safe.open || '10:00',
      close: safe.close || '21:00',
      days: Array.isArray(safe.days) ? safe.days : ['mon','tue','wed','thu','fri','sat'],
      schedule: {},
    };
  }
  // custom
  const sched = safe.schedule || {};
  // Initialize any missing day from the stored value or default to closed
  const filledSchedule = {};
  DAYS.forEach(d => {
    const v = sched[d.id];
    if (v && typeof v.open === 'string' && typeof v.close === 'string') {
      filledSchedule[d.id] = { open: v.open, close: v.close, closed: !!v.closed };
    } else {
      filledSchedule[d.id] = { open: '10:00', close: '21:00', closed: true };
    }
  });
  return {
    mode,
    open: '10:00',
    close: '21:00',
    days: ['mon','tue','wed','thu','fri','sat'],
    schedule: filledSchedule,
  };
}

// Inverse: build the persisted shape from the form state.
function buildHoursObject(form) {
  if (form.mode === '247') return { mode: '247' };
  if (form.mode === 'appointment') return { mode: 'appointment' };
  if (form.mode === 'standard') {
    return {
      mode: 'standard',
      open: form.open || '10:00',
      close: form.close || '21:00',
      days: Array.isArray(form.days) ? form.days : [],
    };
  }
  // custom — keep only days with closed:false; closed:true stays so display logic can still read it
  const schedule = {};
  DAYS.forEach(d => {
    const v = form.schedule[d.id];
    if (!v) return;
    schedule[d.id] = {
      open: v.open,
      close: v.close,
      closed: !!v.closed,
    };
  });
  return { mode: 'custom', schedule };
}

export default function BusinessHoursEditor({ business, onClose, onSetToast, onSaved }) {
  const initialForm = useMemo(() => readHours(business?.hours), [business?.hours]);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  function toggleStandardDay(dayId) {
    setForm(prev => {
      const has = prev.days.includes(dayId);
      return { ...prev, days: has ? prev.days.filter(d => d !== dayId) : [...prev.days, dayId] };
    });
  }

  function updateCustomDay(dayId, patch) {
    setForm(prev => ({
      ...prev,
      schedule: { ...prev.schedule, [dayId]: { ...prev.schedule[dayId], ...patch } },
    }));
  }

  async function handleSave() {
    if (!business?.id) {
      onSetToast?.('No business loaded');
      return;
    }
    setSaving(true);
    try {
      const next = buildHoursObject(form);
      await updateDoc(doc(db, 'businesses', business.id), {
        hours: next,
        updatedAt: serverTimestamp(),
      });
      onSetToast?.('Hours updated');
      onSaved?.(next);
      onClose?.();
    } catch (err) {
      console.error('[BusinessHoursEditor] save failed:', err);
      onSetToast?.(`Couldn't save: ${err.code || err.message || 'try again'}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'var(--bg)', zIndex: 60,
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0,
      }}>
        <button onClick={onClose}
                style={{ width: 36, height: 36, borderRadius: 999, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                aria-label="Close">
          <X size={20} style={{ color: 'var(--ink)' }} />
        </button>
        <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--ink)' }}>Business hours</div>
        <button onClick={handleSave} disabled={saving}
                style={{
                  padding: '8px 14px', borderRadius: 999, fontSize: 13, fontWeight: 500,
                  background: 'var(--ink)', color: '#fff', border: 'none', cursor: 'pointer',
                  opacity: saving ? 0.6 : 1,
                }}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {/* Mode selector */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-soft)', marginBottom: 8 }}>
            How do you operate?
          </div>
          {MODES.map(m => {
            const active = form.mode === m.id;
            return (
              <button key={m.id} onClick={() => setForm(prev => ({ ...prev, mode: m.id }))}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                        padding: '12px 14px', marginBottom: 8, borderRadius: 12,
                        background: active ? 'var(--accent-soft)' : 'var(--surface)',
                        border: '1px solid ' + (active ? 'var(--accent)' : 'var(--border)'),
                        cursor: 'pointer', textAlign: 'left',
                      }}>
                <div style={{
                  width: 18, height: 18, borderRadius: 999, flexShrink: 0,
                  border: '2px solid ' + (active ? 'var(--accent)' : 'var(--border)'),
                  background: active ? 'var(--accent)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {active && <div style={{ width: 6, height: 6, borderRadius: 999, background: '#fff' }} />}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>{m.label}</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-soft)', marginTop: 2 }}>{m.hint}</div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Mode-specific body */}
        {form.mode === 'standard' && (
          <div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-soft)', marginBottom: 8 }}>
              Working days
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
              {DAYS.map(d => {
                const on = form.days.includes(d.id);
                return (
                  <button key={d.id} onClick={() => toggleStandardDay(d.id)}
                          style={{
                            padding: '8px 12px', borderRadius: 999, fontSize: 12, fontWeight: 500,
                            background: on ? 'var(--ink)' : 'var(--surface)',
                            color: on ? '#fff' : 'var(--ink)',
                            border: '1px solid ' + (on ? 'var(--ink)' : 'var(--border)'),
                            cursor: 'pointer',
                          }}>
                    {d.label}
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-soft)', marginBottom: 8 }}>
              Open hours
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <input type="time" value={form.open}
                     onChange={e => setForm(prev => ({ ...prev, open: e.target.value }))}
                     style={inputStyle} />
              <span style={{ color: 'var(--ink-soft)' }}>to</span>
              <input type="time" value={form.close}
                     onChange={e => setForm(prev => ({ ...prev, close: e.target.value }))}
                     style={inputStyle} />
            </div>
          </div>
        )}

        {form.mode === 'custom' && (
          <div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-soft)', marginBottom: 8 }}>
              Hours per day
            </div>
            {DAYS.map(d => {
              const cfg = form.schedule[d.id] || { open: '10:00', close: '21:00', closed: true };
              return (
                <div key={d.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', marginBottom: 6, borderRadius: 10,
                  background: 'var(--surface)', border: '1px solid var(--border)',
                }}>
                  <div style={{ width: 40, fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{d.label}</div>
                  {cfg.closed ? (
                    <>
                      <div style={{ flex: 1, fontSize: 12, color: 'var(--ink-soft)', fontStyle: 'italic' }}>
                        Closed
                      </div>
                      <button onClick={() => updateCustomDay(d.id, { closed: false })}
                              style={smallBtn}>Open</button>
                    </>
                  ) : (
                    <>
                      <input type="time" value={cfg.open}
                             onChange={e => updateCustomDay(d.id, { open: e.target.value })}
                             style={{ ...inputStyle, width: 110, padding: '5px 8px', fontSize: 12 }} />
                      <span style={{ color: 'var(--ink-soft)', fontSize: 11 }}>to</span>
                      <input type="time" value={cfg.close}
                             onChange={e => updateCustomDay(d.id, { close: e.target.value })}
                             style={{ ...inputStyle, width: 110, padding: '5px 8px', fontSize: 12 }} />
                      <button onClick={() => updateCustomDay(d.id, { closed: true })}
                              style={smallBtn}>Closed</button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {form.mode === '247' && (
          <p style={{ fontSize: 13, color: 'var(--ink-soft)', padding: '12px 4px' }}>
            Customers can see your listing and place orders any time of day.
          </p>
        )}

        {form.mode === 'appointment' && (
          <p style={{ fontSize: 13, color: 'var(--ink-soft)', padding: '12px 4px' }}>
            Customers can browse your listing but bookings happen via chat. No fixed open/close hours shown.
          </p>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--ink)',
  fontSize: 14,
  outline: 'none',
  fontFamily: 'inherit',
};

const smallBtn = {
  padding: '5px 10px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 500,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--ink)',
  cursor: 'pointer',
};
